/**
 * Rate Formatting Utilities
 *
 * Functions for formatting and parsing monetary values for payment tracking.
 * All internal values are stored in cents (1500 = $15.00).
 */

import type { RateType } from '../types/models';

/**
 * Format a rate (in cents) with its type for display
 * @param cents - Rate in cents (e.g., 1500 = $15.00)
 * @param type - Rate type (HOURLY or FLAT)
 * @returns Formatted string (e.g., "$15.00/hr" or "$400.00 flat")
 */
export function formatRate(cents: number | null | undefined, type: RateType | null | undefined): string {
  if (cents === null || cents === undefined) return 'Not set';
  const dollars = (cents / 100).toFixed(2);
  if (type === 'FLAT') {
    return `$${dollars} flat`;
  }
  return `$${dollars}/hr`;
}

/**
 * Format a pay amount (in cents) for display
 * @param cents - Pay amount in cents (e.g., 12750 = $127.50)
 * @returns Formatted string (e.g., "$127.50")
 */
export function formatPay(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '-';
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}

/**
 * Convert a dollars string to cents
 * @param value - String value in dollars (e.g., "15.00" or "15")
 * @returns Value in cents, or null if invalid
 */
export function parseDollars(value: string | null | undefined): number | null {
  if (!value || value.trim() === '') return null;
  // Remove dollar sign and commas if present
  const cleaned = value.replace(/[$,]/g, '').trim();
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

/**
 * Convert cents to a dollars string for input fields
 * @param cents - Value in cents
 * @returns String in dollars (e.g., "15.00")
 */
export function centsToDollars(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

/**
 * Format hours worked for display
 * @param hours - Hours as a number or string (e.g., 8.5)
 * @returns Formatted string (e.g., "8.5 hrs")
 */
export function formatHours(hours: number | string | null | undefined): string {
  if (hours === null || hours === undefined) return '-';
  const num = typeof hours === 'string' ? parseFloat(hours) : hours;
  if (isNaN(num)) return '-';
  // Show whole numbers without decimals, otherwise show one decimal
  const formatted = num % 1 === 0 ? num.toString() : num.toFixed(1);
  return `${formatted} hrs`;
}
